import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Globe, Plus, Trash2, Edit, Check, X, ExternalLink,
  LogOut, Home, Building2, Image, DollarSign, Loader2,
  Search, Filter, RefreshCw, AlertCircle, CheckCircle,
  MapPin, Bed, Bath, Square, Download, Calendar,
  Bookmark, BookmarkPlus, Star, Clock, Layers, ChevronDown, ChevronUp,
  ArrowUpDown, ArrowUp, ArrowDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import logger from "@/utils/logger";
import { EditListingModal, SaveSearchModal, BuildingModal } from "@/components/admin/ScraperModals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper: pick badge color based on cap rate threshold
const capRateColor = (capRate) => {
  if (capRate >= 4) return 'bg-emerald-100 text-emerald-700';
  if (capRate >= 3) return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-600';
};

export default function AdminScraper() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [listings, setListings] = useState([]);  // Imported listings
  const [statusFilter, setStatusFilter] = useState("pending");
  
  // Import from URL form
  const [scrapeUrl, setScrapeUrl] = useState("");
  
  // Realtor.com search
  const [realtorSearch, setRealtorSearch] = useState({
    location: "",
    property_type: "condo",
    min_price: "",
    max_price: "",
    min_beds: "",
    min_year_built: "",
    max_year_built: "",
    min_cap_rate: "",
    limit: 20
  });
  const [searchingRealtor, setSearchingRealtor] = useState(false);
  const [realtorResults, setRealtorResults] = useState([]);
  const [importingIds, setImportingIds] = useState(new Set());
  const [resultsSortBy, setResultsSortBy] = useState("capRate"); // capRate, price, sqft, bedrooms
  const [resultsSortOrder, setResultsSortOrder] = useState("desc"); // asc, desc
  
  // StreetEasy import
  const [streetEasyUrl, setStreetEasyUrl] = useState("");
  const [importingStreetEasy, setImportingStreetEasy] = useState(false);
  
  // Krieger NY import
  const [kriegerUrl, setKriegerUrl] = useState("");
  const [importingKrieger, setImportingKrieger] = useState(false);
  
  // Krieger Florida import
  const [kriegerFlUrl, setKriegerFlUrl] = useState("");
  const [importingKriegerFl, setImportingKriegerFl] = useState(false);
  
  // NYC Real Estate API search
  const [nycSearch, setNycSearch] = useState({
    areas: "all-downtown",
    search_type: "sales",
    min_price: "",
    max_price: "",
    min_beds: "",
    max_beds: "",
    property_types: "condo",
    max_hoa: "",
    max_tax: "",
    status: "delisted",
    closed_after: "2024-01-01",
    limit: 50
  });
  const [searchingNyc, setSearchingNyc] = useState(false);
  const [nycResults, setNycResults] = useState([]);
  const [nycAreas, setNycAreas] = useState(null);
  
  // Edit modal
  const [editModal, setEditModal] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [savingManual, setSavingManual] = useState(false);
  const [estimating, setEstimating] = useState(false);

  // Saved searches
  const [savedSearches, setSavedSearches] = useState([]);
  const [saveSearchModal, setSaveSearchModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState("");
  const [savingSearch, setSavingSearch] = useState(false);

  // Building management
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [buildingModal, setBuildingModal] = useState(false);
  const [buildingForm, setBuildingForm] = useState({
    name: "",
    address: "",
    neighborhood: "",
    developer: "",
    year_built: "",
    total_units: "",
    description: ""
  });
  const [creatingBuilding, setCreatingBuilding] = useState(false);
  const [editingBuildingId, setEditingBuildingId] = useState(null); // null = create mode, id = edit mode
  const [buildingUnitUrl, setBuildingUnitUrl] = useState("");
  const [addingUnit, setAddingUnit] = useState(false);
  const [expandedBuilding, setExpandedBuilding] = useState(null);
  const [streetEasyMode, setStreetEasyMode] = useState("single"); // "single" or "building"

  // Rent estimation constants (per SF/month)
  const RENT_RATES = {
    nyc: { default: 4.50, luxury: 5.50 },
    miami: { default: 3.50, luxury: 4.25 }
  };
  const HOA_RATES = {
    nyc: { default: 1.20 },
    miami: { default: 1.00 }
  };
  const TAX_RATES = {
    nyc: 0.008,  // 0.8% annual
    miami: 0.018 // 1.8% annual
  };

  // Estimate financials for a property
  const estimatePropertyFinancials = (property) => {
    const sqft = property.sqft || 0;
    const price = property.price || 0;
    const market = (property.market || "nyc").toLowerCase();
    const bedrooms = property.bedrooms || 2;
    
    if (!sqft || !price) return { rent: 0, hoa: 0, taxes: 0, noi: 0, capRate: 0 };
    
    // Estimate rent
    const rentRate = RENT_RATES[market]?.default || 4.00;
    let bedroomAdj = 1.0;
    if (bedrooms <= 1) bedroomAdj = 1.10;
    else if (bedrooms >= 3) bedroomAdj = 0.95;
    const monthlyRent = Math.round(sqft * rentRate * bedroomAdj / 10) * 10;
    
    // Estimate HOA
    const hoaRate = HOA_RATES[market]?.default || 1.00;
    const monthlyHoa = Math.round(sqft * hoaRate / 10) * 10;
    
    // Estimate taxes
    const taxRate = TAX_RATES[market] || 0.012;
    const monthlyTaxes = Math.round((price * taxRate / 12) / 10) * 10;
    
    // Calculate NOI and cap rate
    const annualRent = monthlyRent * 12;
    const annualExpenses = (monthlyHoa + monthlyTaxes) * 12;
    const noi = annualRent - annualExpenses;
    const capRate = price > 0 ? (noi / price) * 100 : 0;
    
    return {
      rent: monthlyRent,
      hoa: monthlyHoa,
      taxes: monthlyTaxes,
      noi: noi,
      capRate: capRate
    };
  };

  // Sort results helper
  const getSortedResults = useCallback((results) => {
    if (!results || results.length === 0) return [];
    
    return [...results].sort((a, b) => {
      let aVal, bVal;
      
      switch (resultsSortBy) {
        case "capRate":
          aVal = a.capRate || 0;
          bVal = b.capRate || 0;
          break;
        case "price":
          aVal = a.price || 0;
          bVal = b.price || 0;
          break;
        case "sqft":
          aVal = a.sqft || 0;
          bVal = b.sqft || 0;
          break;
        case "bedrooms":
          aVal = a.bedrooms || 0;
          bVal = b.bedrooms || 0;
          break;
        case "pricePerSqft":
          aVal = a.sqft > 0 ? (a.price / a.sqft) : 0;
          bVal = b.sqft > 0 ? (b.price / b.sqft) : 0;
          break;
        case "rent":
          aVal = a.rent || 0;
          bVal = b.rent || 0;
          break;
        default:
          aVal = a.capRate || 0;
          bVal = b.capRate || 0;
      }
      
      return resultsSortOrder === "desc" ? bVal - aVal : aVal - bVal;
    });
  }, [resultsSortBy, resultsSortOrder]);

  // Toggle sort
  const handleSort = (column) => {
    if (resultsSortBy === column) {
      setResultsSortOrder(prev => prev === "desc" ? "asc" : "desc");
    } else {
      setResultsSortBy(column);
      setResultsSortOrder("desc");
    }
  };

  // Check auth
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data?.is_admin) {
          setUser(response.data);
        } else {
          navigate('/admin/login');
        }
      } catch (err) {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  // Fetch listings
  const fetchListings = useCallback(async () => {
    try {
      const url = statusFilter ? `${API}/admin/scraped-listings?status=${statusFilter}` : `${API}/admin/scraped-listings`;
      const response = await axios.get(url, { withCredentials: true });
      setListings(response.data);
    } catch (err) {
      logger.error("Failed to fetch imported listings:", err);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (user) {
      fetchListings();
    }
  }, [user, fetchListings]);

  // Fetch saved searches
  const fetchSavedSearches = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/saved-searches`, { withCredentials: true });
      setSavedSearches(response.data);
    } catch (err) {
      logger.error("Failed to fetch saved searches:", err);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchSavedSearches();
    }
  }, [user, fetchSavedSearches]);

  // Save current search
  const handleSaveSearch = async () => {
    if (!saveSearchName.trim()) {
      toast.error("Please enter a name for this search");
      return;
    }
    if (!realtorSearch.location.trim()) {
      toast.error("Please enter a location first");
      return;
    }
    
    setSavingSearch(true);
    try {
      await axios.post(`${API}/admin/saved-searches`, {
        name: saveSearchName,
        location: realtorSearch.location,
        property_type: realtorSearch.property_type || null,
        min_price: realtorSearch.min_price ? parseInt(realtorSearch.min_price) : null,
        max_price: realtorSearch.max_price ? parseInt(realtorSearch.max_price) : null,
        min_beds: realtorSearch.min_beds ? parseInt(realtorSearch.min_beds) : null,
        min_year_built: realtorSearch.min_year_built ? parseInt(realtorSearch.min_year_built) : null,
        max_year_built: realtorSearch.max_year_built ? parseInt(realtorSearch.max_year_built) : null,
        min_cap_rate: realtorSearch.min_cap_rate ? parseFloat(realtorSearch.min_cap_rate) : null,
        limit: realtorSearch.limit || 20
      }, { withCredentials: true });
      
      toast.success("Search saved!");
      setSaveSearchModal(false);
      setSaveSearchName("");
      fetchSavedSearches();
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to save search";
      toast.error(message);
    } finally {
      setSavingSearch(false);
    }
  };

  // Load a saved search
  const handleLoadSearch = async (search) => {
    setRealtorSearch({
      location: search.location || "",
      property_type: search.property_type || "condo",
      min_price: search.min_price?.toString() || "",
      max_price: search.max_price?.toString() || "",
      min_beds: search.min_beds?.toString() || "",
      min_year_built: search.min_year_built?.toString() || "",
      max_year_built: search.max_year_built?.toString() || "",
      min_cap_rate: search.min_cap_rate?.toString() || "",
      limit: search.limit || 20
    });
    
    // Mark as recently used
    try {
      await axios.put(`${API}/admin/saved-searches/${search.search_id}/use`, {}, { withCredentials: true });
      fetchSavedSearches();
    } catch (err) {
      logger.error("Failed to update search usage:", err);
    }
    
    toast.success(`Loaded "${search.name}" - click Search to run`);
  };

  // Delete a saved search
  const handleDeleteSearch = async (searchId, searchName) => {
    if (!confirm(`Delete saved search "${searchName}"?`)) return;
    
    try {
      await axios.delete(`${API}/admin/saved-searches/${searchId}`, { withCredentials: true });
      toast.success("Search deleted");
      fetchSavedSearches();
    } catch (err) {
      toast.error("Failed to delete search");
    }
  };

  // Fetch buildings
  const fetchBuildings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/buildings`, { withCredentials: true });
      setBuildings(response.data);
    } catch (err) {
      logger.error("Failed to fetch buildings:", err);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchBuildings();
      fetchNycAreas();
    }
  }, [user, fetchBuildings]);

  // Create a new building
  const handleCreateBuilding = async () => {
    if (!buildingForm.name.trim() || !buildingForm.address.trim()) {
      toast.error("Building name and address are required");
      return;
    }
    
    setCreatingBuilding(true);
    try {
      const response = await axios.post(`${API}/admin/buildings`, {
        name: buildingForm.name,
        address: buildingForm.address,
        neighborhood: buildingForm.neighborhood || null,
        developer: buildingForm.developer || null,
        year_built: buildingForm.year_built ? parseInt(buildingForm.year_built) : null,
        total_units: buildingForm.total_units ? parseInt(buildingForm.total_units) : null,
        description: buildingForm.description || null
      }, { withCredentials: true });
      
      toast.success("Building created!");
      closeBuildingModal();
      fetchBuildings();
      
      // Auto-select the new building
      setSelectedBuilding(response.data.building_id);
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to create building";
      toast.error(message);
    } finally {
      setCreatingBuilding(false);
    }
  };

  // Update an existing building
  const handleUpdateBuilding = async () => {
    if (!editingBuildingId) return;
    if (!buildingForm.name.trim() || !buildingForm.address.trim()) {
      toast.error("Building name and address are required");
      return;
    }
    
    setCreatingBuilding(true);
    try {
      await axios.put(`${API}/admin/buildings/${editingBuildingId}`, {
        name: buildingForm.name,
        address: buildingForm.address,
        neighborhood: buildingForm.neighborhood || null,
        developer: buildingForm.developer || null,
        year_built: buildingForm.year_built ? parseInt(buildingForm.year_built) : null,
        total_units: buildingForm.total_units ? parseInt(buildingForm.total_units) : null,
        description: buildingForm.description || null
      }, { withCredentials: true });
      
      toast.success("Building updated!");
      closeBuildingModal();
      fetchBuildings();
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to update building";
      toast.error(message);
    } finally {
      setCreatingBuilding(false);
    }
  };

  // Open modal for editing
  const openEditBuildingModal = (building) => {
    setEditingBuildingId(building.building_id);
    setBuildingForm({
      name: building.name || "",
      address: building.address || "",
      neighborhood: building.neighborhood || "",
      developer: building.developer || "",
      year_built: building.year_built?.toString() || "",
      total_units: building.total_units?.toString() || "",
      description: building.description || ""
    });
    setBuildingModal(true);
  };

  // Close modal and reset state
  const closeBuildingModal = () => {
    setBuildingModal(false);
    setEditingBuildingId(null);
    setBuildingForm({ name: "", address: "", neighborhood: "", developer: "", year_built: "", total_units: "", description: "" });
  };

  // Fetch building with units
  const fetchBuildingDetails = async (buildingId) => {
    try {
      const response = await axios.get(`${API}/admin/buildings/${buildingId}`, { withCredentials: true });
      return response.data;
    } catch (err) {
      logger.error("Failed to fetch building details:", err);
      return null;
    }
  };

  // Add unit to building
  const handleAddUnitToBuilding = async () => {
    if (!selectedBuilding) {
      toast.error("Please select a building first");
      return;
    }
    if (!buildingUnitUrl.trim()) {
      toast.error("Please enter a StreetEasy listing URL");
      return;
    }
    if (!buildingUnitUrl.includes("streeteasy.com")) {
      toast.error("Please enter a valid StreetEasy URL");
      return;
    }
    
    setAddingUnit(true);
    try {
      const response = await axios.post(
        `${API}/admin/buildings/${selectedBuilding}/add-unit?unit_url=${encodeURIComponent(buildingUnitUrl)}`,
        {},
        { withCredentials: true }
      );
      
      toast.success(`Unit added! Cap Rate: ${response.data.data?.estimated_cap_rate || "N/A"}%`);
      setBuildingUnitUrl("");
      fetchBuildings();
      fetchListings();
      
      // Refresh expanded building view if open
      if (expandedBuilding === selectedBuilding) {
        const details = await fetchBuildingDetails(selectedBuilding);
        if (details) {
          setBuildings(prev => prev.map(b => 
            b.building_id === selectedBuilding ? { ...b, ...details } : b
          ));
        }
      }
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to add unit";
      toast.error(message);
    } finally {
      setAddingUnit(false);
    }
  };

  // Toggle building expansion to show units
  const toggleBuildingExpand = async (buildingId) => {
    if (expandedBuilding === buildingId) {
      setExpandedBuilding(null);
    } else {
      setExpandedBuilding(buildingId);
      const details = await fetchBuildingDetails(buildingId);
      if (details) {
        setBuildings(prev => prev.map(b => 
          b.building_id === buildingId ? { ...b, ...details } : b
        ));
      }
    }
  };

  // Delete building
  const handleDeleteBuilding = async (buildingId, buildingName) => {
    if (!confirm(`Delete building "${buildingName}"? Units will be unlinked but not deleted.`)) return;
    
    try {
      await axios.delete(`${API}/admin/buildings/${buildingId}`, { withCredentials: true });
      toast.success("Building deleted");
      fetchBuildings();
      if (selectedBuilding === buildingId) {
        setSelectedBuilding(null);
      }
      if (expandedBuilding === buildingId) {
        setExpandedBuilding(null);
      }
    } catch (err) {
      toast.error("Failed to delete building");
    }
  };

  // Remove unit from building (keeps the listing)
  const handleRemoveUnitFromBuilding = async (scrapedId, unitNumber, buildingId) => {
    if (!confirm(`Remove Unit ${unitNumber || "?"} from this building? The listing will be preserved.`)) return;
    
    try {
      await axios.post(`${API}/admin/scraped-listings/${scrapedId}/unlink-building`, {}, { withCredentials: true });
      toast.success("Unit removed from building");
      fetchBuildings();
      fetchListings();
      
      // Refresh expanded building view
      if (expandedBuilding === buildingId) {
        const details = await fetchBuildingDetails(buildingId);
        if (details) {
          setBuildings(prev => prev.map(b => 
            b.building_id === buildingId ? { ...b, ...details } : b
          ));
        }
      }
    } catch (err) {
      toast.error("Failed to remove unit");
    }
  };

  // Scrape a new listing
  const handleScrape = async (e) => {
    e.preventDefault();
    if (!scrapeUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    
    setScraping(true);
    try {
      const response = await axios.post(`${API}/admin/scrape`, {
        url: scrapeUrl
      }, { withCredentials: true });
      
      toast.success("Listing imported successfully!");
      setScrapeUrl("");
      fetchListings();
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to import listing";
      toast.error(message);
      logger.error("Import error:", err);
    } finally {
      setScraping(false);
    }
  };

  // Open manual entry modal
  const openManualEntry = () => {
    setIsManualEntry(true);
    setEditingListing(null);
    setEditForm({
      title: "",
      address: "",
      unit_number: "",
      building_name: "",
      neighborhood: "",
      city: "",
      market: "nyc",
      price: "",
      bedrooms: "",
      bathrooms: "",
      sqft: "",
      monthly_common_charges: "",
      monthly_taxes: "",
      projected_monthly_rent: "",
      description: "",
      sponsor_name: "",
      source_url: "",
      year_built: "",
    });
    setEditModal(true);
  };

  // Save manual entry as a new imported listing
  const handleSaveManualEntry = async () => {
    if (!editForm.title && !editForm.address) {
      toast.error("Please enter at least a title or address");
      return;
    }
    
    setSavingManual(true);
    try {
      const response = await axios.post(`${API}/admin/scraped-listings/manual`, {
        ...editForm,
        price: editForm.price ? parseFloat(editForm.price) : null,
        bedrooms: editForm.bedrooms ? parseInt(editForm.bedrooms) : null,
        bathrooms: editForm.bathrooms ? parseFloat(editForm.bathrooms) : null,
        sqft: editForm.sqft ? parseInt(editForm.sqft) : null,
        year_built: editForm.year_built ? parseInt(editForm.year_built) : null,
        monthly_common_charges: editForm.monthly_common_charges ? parseFloat(editForm.monthly_common_charges) : null,
        monthly_taxes: editForm.monthly_taxes ? parseFloat(editForm.monthly_taxes) : null,
        projected_monthly_rent: editForm.projected_monthly_rent ? parseFloat(editForm.projected_monthly_rent) : null,
      }, { withCredentials: true });
      
      toast.success("Listing created successfully!");
      setEditModal(false);
      setIsManualEntry(false);
      fetchListings();
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to create listing";
      toast.error(message);
      logger.error("Manual entry error:", err);
    } finally {
      setSavingManual(false);
    }
  };

  // Search Realtor.com
  const handleRealtorSearch = async (e) => {
    e.preventDefault();
    if (!realtorSearch.location.trim()) {
      toast.error("Please enter a location to search");
      return;
    }
    
    setSearchingRealtor(true);
    setRealtorResults([]);
    
    try {
      const params = {
        location: realtorSearch.location,
        limit: realtorSearch.limit || 20
      };
      if (realtorSearch.property_type) params.property_type = realtorSearch.property_type;
      if (realtorSearch.min_price) params.min_price = parseInt(realtorSearch.min_price);
      if (realtorSearch.max_price) params.max_price = parseInt(realtorSearch.max_price);
      if (realtorSearch.min_beds) params.min_beds = parseInt(realtorSearch.min_beds);
      if (realtorSearch.min_year_built) params.min_year_built = parseInt(realtorSearch.min_year_built);
      if (realtorSearch.max_year_built) params.max_year_built = parseInt(realtorSearch.max_year_built);
      
      const response = await axios.post(`${API}/admin/realtor/search`, params, { withCredentials: true });
      
      let results = response.data.properties || [];
      
      // Filter by min cap rate if specified
      if (realtorSearch.min_cap_rate) {
        const minCapRate = parseFloat(realtorSearch.min_cap_rate);
        results = results.filter(property => {
          const financials = estimatePropertyFinancials(property);
          return financials.capRate >= minCapRate;
        });
      }
      
      setRealtorResults(results);
      toast.success(`Found ${results.length} properties${realtorSearch.min_cap_rate ? ` with ${realtorSearch.min_cap_rate}%+ cap rate` : ''}`);
    } catch (err) {
      const message = err.response?.data?.detail || "Search failed";
      toast.error(message);
      logger.error("Realtor search error:", err);
    } finally {
      setSearchingRealtor(false);
    }
  };

  // Import property from Realtor.com search results
  const handleImportRealtor = async (property) => {
    const propertyId = property.property_id;
    setImportingIds(prev => new Set([...prev, propertyId]));
    
    try {
      await axios.post(`${API}/admin/realtor/import-from-search`, property, { withCredentials: true });
      toast.success("Property imported successfully!");
      fetchListings();
      // Remove from results
      setRealtorResults(prev => prev.filter(p => p.property_id !== propertyId));
    } catch (err) {
      const message = err.response?.data?.detail || "Import failed";
      toast.error(message);
      logger.error("Realtor import error:", err);
    } finally {
      setImportingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(propertyId);
        return newSet;
      });
    }
  };

  // NYC Real Estate API Search
  const handleNycSearch = async (e) => {
    e.preventDefault();
    if (!nycSearch.areas.trim()) {
      toast.error("Please select an area to search");
      return;
    }
    
    setSearchingNyc(true);
    setNycResults([]);
    
    try {
      const params = {
        areas: nycSearch.areas,
        search_type: nycSearch.search_type,
        limit: nycSearch.limit || 50
      };
      if (nycSearch.min_price) params.min_price = parseInt(nycSearch.min_price);
      if (nycSearch.max_price) params.max_price = parseInt(nycSearch.max_price);
      if (nycSearch.min_beds) params.min_beds = parseInt(nycSearch.min_beds);
      if (nycSearch.max_beds) params.max_beds = parseInt(nycSearch.max_beds);
      if (nycSearch.property_types) params.property_types = nycSearch.property_types;
      if (nycSearch.max_hoa) params.max_hoa = parseInt(nycSearch.max_hoa);
      if (nycSearch.max_tax) params.max_tax = parseInt(nycSearch.max_tax);
      if (nycSearch.search_type === "sales/past") {
        if (nycSearch.status) params.status = nycSearch.status;
        if (nycSearch.closed_after) params.closed_after = nycSearch.closed_after;
      }
      
      const response = await axios.post(`${API}/admin/nyc/search`, params, { withCredentials: true });
      
      const results = response.data.properties || [];
      setNycResults(results);
      toast.success(`Found ${response.data.total} NYC properties (showing ${results.length})`);
    } catch (err) {
      const message = err.response?.data?.detail || "NYC search failed";
      toast.error(message);
      logger.error("NYC search error:", err);
    } finally {
      setSearchingNyc(false);
    }
  };

  // Fetch NYC areas on mount
  const fetchNycAreas = async () => {
    try {
      const response = await axios.get(`${API}/admin/nyc/areas`, { withCredentials: true });
      setNycAreas(response.data);
    } catch (err) {
      logger.error("Failed to fetch NYC areas:", err);
    }
  };

  // Import property from NYC search results
  const handleImportNyc = async (property) => {
    const propertyId = property.property_id;
    setImportingIds(prev => new Set([...prev, propertyId]));
    
    try {
      // For NYC properties, we import them as scraped_listings since we have limited data
      const listingData = {
        title: property.title || property.address,
        market: "nyc",
        price: property.price,
        sqft: property.sqft,
        bedrooms: property.bedrooms || 2,  // Default estimate
        bathrooms: property.bathrooms || 1,
        source_url: property.url,
        source_site: "nyc-real-estate-api",
        status: "pending",
        projected_monthly_rent: property.rent,
        monthly_common_charges: property.monthly_common_charges,
        monthly_taxes: property.monthly_taxes,
        noi: property.noi,
        cap_rate: property.capRate,
        images: property.images || [],
        latitude: property.latitude,
        longitude: property.longitude
      };
      
      await axios.post(`${API}/admin/scraped-listings`, listingData, { withCredentials: true });
      toast.success("NYC property imported successfully!");
      fetchListings();
      // Remove from results
      setNycResults(prev => prev.filter(p => p.property_id !== propertyId));
    } catch (err) {
      const message = err.response?.data?.detail || "Import failed";
      toast.error(message);
      logger.error("NYC import error:", err);
    } finally {
      setImportingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(propertyId);
        return newSet;
      });
    }
  };

  // Import from StreetEasy by URL
  const handleStreetEasyImport = async (e) => {
    e.preventDefault();
    if (!streetEasyUrl.trim()) {
      toast.error("Please enter a StreetEasy listing URL");
      return;
    }
    
    if (!streetEasyUrl.includes("streeteasy.com")) {
      toast.error("Please enter a valid StreetEasy URL");
      return;
    }
    
    setImportingStreetEasy(true);
    
    try {
      const response = await axios.post(`${API}/admin/streeteasy/import`, {
        url: streetEasyUrl
      }, { withCredentials: true });
      
      toast.success("StreetEasy listing imported successfully!");
      setStreetEasyUrl("");
      fetchListings();
    } catch (err) {
      const message = err.response?.data?.detail || "Import failed";
      toast.error(message);
      logger.error("StreetEasy import error:", err);
    } finally {
      setImportingStreetEasy(false);
    }
  };

  // Import from Krieger NY by URL
  const handleKriegerImport = async (e) => {
    e.preventDefault();
    if (!kriegerUrl.trim()) {
      toast.error("Please enter a Krieger NY listing URL");
      return;
    }
    
    if (!kriegerUrl.includes("kriegerny.com")) {
      toast.error("Please enter a valid kriegerny.com URL");
      return;
    }
    
    setImportingKrieger(true);
    
    try {
      const response = await axios.post(`${API}/admin/krieger/import`, {
        url: kriegerUrl
      }, { withCredentials: true });
      
      const data = response.data.data;
      toast.success(`Imported: ${data.title} - $${data.price?.toLocaleString()}`);
      setKriegerUrl("");
      fetchListings();
    } catch (err) {
      const message = err.response?.data?.detail || "Import failed";
      toast.error(message);
      logger.error("Krieger import error:", err);
    } finally {
      setImportingKrieger(false);
    }
  };

  // Import from Krieger Florida by URL
  const handleKriegerFlImport = async (e) => {
    e.preventDefault();
    if (!kriegerFlUrl.trim()) {
      toast.error("Please enter a Krieger Florida listing URL");
      return;
    }
    
    if (!kriegerFlUrl.includes("kriegerflorida.com")) {
      toast.error("Please enter a valid kriegerflorida.com URL");
      return;
    }
    
    setImportingKriegerFl(true);
    
    try {
      const response = await axios.post(`${API}/admin/krieger-florida/import`, {
        url: kriegerFlUrl
      }, { withCredentials: true });
      
      const data = response.data.data;
      toast.success(`Imported: ${data.title} - $${data.price?.toLocaleString()}`);
      setKriegerFlUrl("");
      fetchListings();
    } catch (err) {
      const message = err.response?.data?.detail || "Import failed";
      toast.error(message);
      logger.error("Krieger Florida import error:", err);
    } finally {
      setImportingKriegerFl(false);
    }
  };

  // Open edit modal
  const openEditModal = (listing) => {
    setIsManualEntry(false);
    setEditingListing(listing);
    setEditForm({
      title: listing.title || "",
      address: listing.address || "",
      unit_number: listing.unit_number || "",
      building_name: listing.building_name || "",
      neighborhood: listing.neighborhood || "",
      city: listing.city || "",
      market: listing.market || "nyc",
      price: listing.price || "",
      bedrooms: listing.bedrooms || "",
      bathrooms: listing.bathrooms || "",
      sqft: listing.sqft || "",
      year_built: listing.year_built || "",
      monthly_common_charges: listing.monthly_common_charges || "",
      monthly_taxes: listing.monthly_taxes || "",
      projected_monthly_rent: listing.projected_monthly_rent || "",
      description: listing.description || "",
      sponsor_name: listing.sponsor_name || "",
    });
    setEditModal(true);
  };

  // Calculate estimated cap rate
  const calculateCapRate = () => {
    const price = parseFloat(editForm.price) || 0;
    const monthlyRent = parseFloat(editForm.projected_monthly_rent) || 0;
    const monthlyCharges = parseFloat(editForm.monthly_common_charges) || 0;
    const monthlyTaxes = parseFloat(editForm.monthly_taxes) || 0;
    
    if (price <= 0 || monthlyRent <= 0) return null;
    
    const annualRent = monthlyRent * 12;
    const annualExpenses = (monthlyCharges + monthlyTaxes) * 12;
    const netOperatingIncome = annualRent - annualExpenses;
    const capRate = (netOperatingIncome / price) * 100;
    
    return capRate.toFixed(2);
  };

  // Auto-estimate financials
  const handleAutoEstimate = async () => {
    if (!editingListing?.scraped_id && !isManualEntry) return;
    
    setEstimating(true);
    try {
      if (isManualEntry) {
        // For manual entry, call the estimate endpoint with form data
        const response = await axios.post(`${API}/admin/estimate-rent`, {
          sqft: editForm.sqft ? parseInt(editForm.sqft) : null,
          market: editForm.market || "nyc",
          zip_code: editForm.zip_code,
          bedrooms: editForm.bedrooms ? parseInt(editForm.bedrooms) : null,
          price: editForm.price ? parseFloat(editForm.price) : null,
        }, { withCredentials: true });
        
        const estimates = response.data;
        setEditForm(prev => ({
          ...prev,
          projected_monthly_rent: estimates.projected_monthly_rent || prev.projected_monthly_rent,
          monthly_common_charges: estimates.monthly_common_charges || prev.monthly_common_charges,
          monthly_taxes: estimates.monthly_taxes || prev.monthly_taxes,
        }));
        toast.success("Estimates calculated!");
      } else {
        // For existing listing, call the estimate endpoint
        const response = await axios.post(
          `${API}/admin/scraped-listings/${editingListing.scraped_id}/estimate`,
          {},
          { withCredentials: true }
        );
        
        const { estimates, financials } = response.data;
        setEditForm(prev => ({
          ...prev,
          projected_monthly_rent: financials.projected_monthly_rent || prev.projected_monthly_rent,
          monthly_common_charges: financials.monthly_common_charges || prev.monthly_common_charges,
          monthly_taxes: financials.monthly_taxes || prev.monthly_taxes,
        }));
        toast.success(`Estimates calculated! Cap Rate: ${financials.cap_rate}%`);
      }
    } catch (err) {
      toast.error("Failed to estimate financials");
      logger.error("Estimate error:", err);
    } finally {
      setEstimating(false);
    }
  };

  // Save edit
  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/admin/scraped-listings/${editingListing.scraped_id}`, {
        ...editForm,
        price: editForm.price ? parseFloat(editForm.price) : null,
        bedrooms: editForm.bedrooms ? parseInt(editForm.bedrooms) : null,
        bathrooms: editForm.bathrooms ? parseFloat(editForm.bathrooms) : null,
        sqft: editForm.sqft ? parseInt(editForm.sqft) : null,
        year_built: editForm.year_built ? parseInt(editForm.year_built) : null,
        monthly_common_charges: editForm.monthly_common_charges ? parseFloat(editForm.monthly_common_charges) : null,
        monthly_taxes: editForm.monthly_taxes ? parseFloat(editForm.monthly_taxes) : null,
        projected_monthly_rent: editForm.projected_monthly_rent ? parseFloat(editForm.projected_monthly_rent) : null,
      }, { withCredentials: true });
      
      toast.success("Listing updated");
      setEditModal(false);
      fetchListings();
    } catch (err) {
      toast.error("Failed to update listing");
      logger.error("Update error:", err);
    }
  };

  // Approve listing
  const handleApprove = async (scrapedId) => {
    try {
      await axios.post(`${API}/admin/scraped-listings/${scrapedId}/approve`, {}, { withCredentials: true });
      toast.success("Listing approved and added to properties!");
      fetchListings();
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to approve listing";
      toast.error(message);
      logger.error("Approve error:", err);
    }
  };

  // Reject listing
  const handleReject = async (scrapedId) => {
    if (!window.confirm("Are you sure you want to reject this listing?")) return;
    
    try {
      await axios.post(`${API}/admin/scraped-listings/${scrapedId}/reject`, {}, { withCredentials: true });
      toast.success("Listing rejected");
      fetchListings();
    } catch (err) {
      toast.error("Failed to reject listing");
      logger.error("Reject error:", err);
    }
  };

  // Delete listing
  const handleDelete = async (scrapedId) => {
    if (!window.confirm("Are you sure you want to delete this listing?")) return;
    
    try {
      await axios.delete(`${API}/admin/scraped-listings/${scrapedId}`, { withCredentials: true });
      toast.success("Listing deleted");
      fetchListings();
    } catch (err) {
      toast.error("Failed to delete listing");
      logger.error("Delete error:", err);
    }
  };

  const formatPrice = (price) => {
    if (!price) return "—";
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 text-white px-6 py-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <span className="text-slate-600">/</span>
            <h1 className="text-xl font-bold tracking-tight">Listing Importer</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/properties">
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-white/10">
                <Building2 className="w-4 h-4 mr-2" />
                Properties
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Import Methods - Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-slate-900">Import Properties</h2>
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">Choose import method below</span>
          </div>
          
          <Tabs defaultValue="krieger" className="w-full">
            <TabsList className="grid w-full grid-cols-7 mb-6 bg-slate-100 p-1 rounded-lg">
              <TabsTrigger value="krieger" className="flex items-center gap-1 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-md font-medium text-xs px-2">
                <Home className="w-3 h-3" />
                Krieger NY
              </TabsTrigger>
              <TabsTrigger value="kriegerfl" className="flex items-center gap-1 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-md font-medium text-xs px-2">
                <Home className="w-3 h-3" />
                Krieger FL
              </TabsTrigger>
              <TabsTrigger value="nyc" className="flex items-center gap-1 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-md font-medium text-xs px-2">
                <MapPin className="w-3 h-3" />
                NYC Search
              </TabsTrigger>
              <TabsTrigger value="realtor" className="flex items-center gap-1 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-md font-medium text-xs px-2">
                <Search className="w-3 h-3" />
                Realtor.com
              </TabsTrigger>
              <TabsTrigger value="streeteasy" className="flex items-center gap-1 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-md font-medium text-xs px-2">
                <Building2 className="w-3 h-3" />
                StreetEasy
              </TabsTrigger>
              <TabsTrigger value="url" className="flex items-center gap-1 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-md font-medium text-xs px-2">
                <Globe className="w-3 h-3" />
                URL Import
              </TabsTrigger>
              <TabsTrigger value="manual" className="flex items-center gap-1 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-md font-medium text-xs px-2">
                <Edit className="w-3 h-3" />
                Manual
              </TabsTrigger>
            </TabsList>

            {/* Krieger NY Import Tab */}
            <TabsContent value="krieger">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Home className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#1A1A2E]">Import from Krieger NY</h2>
                  <p className="text-sm text-slate-500">Import listings directly from kriegerny.com with photos and financials</p>
                </div>
              </div>
              
              <form onSubmit={handleKriegerImport} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Krieger NY Listing URL</label>
                  <div className="flex gap-3">
                    <Input
                      value={kriegerUrl}
                      onChange={(e) => setKriegerUrl(e.target.value)}
                      placeholder="https://www.kriegerny.com/midtown/condo-for-sale/..."
                      className="flex-1"
                      disabled={importingKrieger}
                      data-testid="krieger-url-input"
                    />
                    <Button 
                      type="submit" 
                      disabled={importingKrieger}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid="krieger-import-btn"
                    >
                      {importingKrieger ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Import Listing
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </form>
              
              <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <h4 className="font-semibold text-indigo-800 mb-2">What gets imported:</h4>
                <ul className="text-sm text-indigo-700 space-y-1">
                  <li>• <strong>Price</strong> - Listing price</li>
                  <li>• <strong>Beds & Baths</strong> - Bedroom and bathroom count</li>
                  <li>• <strong>Square Footage</strong> - Total sq ft</li>
                  <li>• <strong>Monthly Tax</strong> - Real estate tax</li>
                  <li>• <strong>Common Charges</strong> - HOA/maintenance fees</li>
                  <li>• <strong>Photos</strong> - Up to 10 listing photos</li>
                  <li>• <strong>Financials</strong> - Auto-calculated rent estimate, NOI, and cap rate</li>
                </ul>
              </div>
              
              <p className="mt-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 p-3 rounded-lg">
                <CheckCircle className="w-4 h-4 inline mr-2" />
                Direct import from your Krieger NY website. Imported listings will appear in "Pending" for review.
              </p>
            </TabsContent>

            {/* Krieger Florida Import Tab */}
            <TabsContent value="kriegerfl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Home className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#1A1A2E]">Import from Krieger Florida</h2>
                  <p className="text-sm text-slate-500">Import MLS listings from kriegerflorida.com with photos and financials</p>
                </div>
              </div>
              
              <form onSubmit={handleKriegerFlImport} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Krieger Florida Property URL</label>
                  <div className="flex gap-3">
                    <Input
                      value={kriegerFlUrl}
                      onChange={(e) => setKriegerFlUrl(e.target.value)}
                      placeholder="https://kriegerflorida.com/property/ADDRESS-MLS/"
                      className="flex-1"
                      disabled={importingKriegerFl}
                      data-testid="kriegerfl-url-input"
                    />
                    <Button 
                      type="submit" 
                      disabled={importingKriegerFl}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                      data-testid="kriegerfl-import-btn"
                    >
                      {importingKriegerFl ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Import Listing
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Use the direct property page URL (e.g., kriegerflorida.com/property/address-mls/)
                  </p>
                </div>
              </form>
              
              <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h4 className="font-semibold text-orange-800 mb-2">What gets imported:</h4>
                <ul className="text-sm text-orange-700 space-y-1">
                  <li>• <strong>Price</strong> - Listing price</li>
                  <li>• <strong>Beds & Baths</strong> - Bedroom and bathroom count</li>
                  <li>• <strong>Square Footage</strong> - Total sq ft (if available)</li>
                  <li>• <strong>HOA Fees</strong> - Monthly maintenance/condo fees</li>
                  <li>• <strong>MLS Number</strong> - Miami MLS ID</li>
                  <li>• <strong>Financials</strong> - Auto-calculated rent estimate, NOI, and cap rate</li>
                </ul>
              </div>
              
              <p className="mt-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 p-3 rounded-lg">
                <CheckCircle className="w-4 h-4 inline mr-2" />
                Click on a property listing, then copy the URL from your browser's address bar. Imported listings appear in "Pending".
              </p>
            </TabsContent>

            {/* NYC Real Estate API Tab */}
            <TabsContent value="nyc">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#1A1A2E]">NYC Real Estate Search</h2>
                  <p className="text-sm text-slate-500">Search active listings and past sales in NYC neighborhoods</p>
                </div>
              </div>
              
              <form onSubmit={handleNycSearch} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Search Type */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Search Type</label>
                    <Select
                      value={nycSearch.search_type}
                      onValueChange={(v) => setNycSearch({...nycSearch, search_type: v})}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Search type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sales">Active Sales</SelectItem>
                        <SelectItem value="sales/past">Past Sales (Closed/Delisted)</SelectItem>
                        <SelectItem value="rentals">Rentals</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Area */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Neighborhood / Area</label>
                    <Select
                      value={nycSearch.areas}
                      onValueChange={(v) => setNycSearch({...nycSearch, areas: v})}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select area" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all-downtown">All Downtown Manhattan</SelectItem>
                        <SelectItem value="all-midtown">All Midtown Manhattan</SelectItem>
                        <SelectItem value="manhattan">All Manhattan</SelectItem>
                        <SelectItem value="tribeca">Tribeca</SelectItem>
                        <SelectItem value="soho">SoHo</SelectItem>
                        <SelectItem value="chelsea">Chelsea</SelectItem>
                        <SelectItem value="greenwich-village">Greenwich Village</SelectItem>
                        <SelectItem value="east-village">East Village</SelectItem>
                        <SelectItem value="flatiron">Flatiron</SelectItem>
                        <SelectItem value="gramercy-park">Gramercy Park</SelectItem>
                        <SelectItem value="upper-east-side">Upper East Side</SelectItem>
                        <SelectItem value="upper-west-side">Upper West Side</SelectItem>
                        <SelectItem value="financial-district">Financial District</SelectItem>
                        <SelectItem value="battery-park-city">Battery Park City</SelectItem>
                        <SelectItem value="brooklyn">All Brooklyn</SelectItem>
                        <SelectItem value="williamsburg">Williamsburg</SelectItem>
                        <SelectItem value="dumbo">DUMBO</SelectItem>
                        <SelectItem value="brooklyn-heights">Brooklyn Heights</SelectItem>
                        <SelectItem value="park-slope">Park Slope</SelectItem>
                        <SelectItem value="queens">All Queens</SelectItem>
                        <SelectItem value="long-island-city">Long Island City</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Property Type */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Property Type</label>
                    <Select
                      value={nycSearch.property_types}
                      onValueChange={(v) => setNycSearch({...nycSearch, property_types: v})}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Property type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="condo">Condo</SelectItem>
                        <SelectItem value="coop">Co-op</SelectItem>
                        <SelectItem value="townhouse">Townhouse</SelectItem>
                        <SelectItem value="house">House</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Min Price</label>
                    <Input
                      type="number"
                      placeholder="500000"
                      value={nycSearch.min_price}
                      onChange={(e) => setNycSearch({...nycSearch, min_price: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Max Price</label>
                    <Input
                      type="number"
                      placeholder="5000000"
                      value={nycSearch.max_price}
                      onChange={(e) => setNycSearch({...nycSearch, max_price: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Min Beds</label>
                    <Input
                      type="number"
                      placeholder="1"
                      value={nycSearch.min_beds}
                      onChange={(e) => setNycSearch({...nycSearch, min_beds: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Max Beds</label>
                    <Input
                      type="number"
                      placeholder="4"
                      value={nycSearch.max_beds}
                      onChange={(e) => setNycSearch({...nycSearch, max_beds: e.target.value})}
                    />
                  </div>
                </div>
                
                {/* Past Sales specific filters */}
                {nycSearch.search_type === "sales/past" && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="col-span-2 md:col-span-3">
                      <p className="text-sm font-medium text-amber-800 mb-2">Past Sales Filters</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                      <Select
                        value={nycSearch.status}
                        onValueChange={(v) => setNycSearch({...nycSearch, status: v})}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="delisted">Delisted</SelectItem>
                          <SelectItem value="contract">In Contract</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Closed After</label>
                      <Input
                        type="date"
                        value={nycSearch.closed_after}
                        onChange={(e) => setNycSearch({...nycSearch, closed_after: e.target.value})}
                      />
                    </div>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <Button 
                    type="submit" 
                    disabled={searchingNyc}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {searchingNyc ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Searching NYC...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Search NYC Properties
                      </>
                    )}
                  </Button>
                </div>
              </form>
              
              {/* NYC Results */}
              {nycResults.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-900">
                      NYC Results ({nycResults.length} properties)
                    </h3>
                  </div>
                  
                  {/* Results Table */}
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-2 p-3 bg-slate-800 text-white text-xs font-medium">
                      <div className="col-span-3">Property</div>
                      <div className="col-span-2 text-right">Price</div>
                      <div className="col-span-1 text-center">Est. SF</div>
                      <div className="col-span-2 text-right">Est. Rent</div>
                      <div className="col-span-2 text-right">Est. Cap Rate</div>
                      <div className="col-span-2 text-right">Action</div>
                    </div>
                    
                    {/* Results */}
                    <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                      {nycResults.map((property) => (
                        <div key={property.property_id} className="grid grid-cols-12 gap-2 p-3 items-center hover:bg-slate-50 text-sm">
                          <div className="col-span-3">
                            <a 
                              href={property.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="font-medium text-blue-600 hover:underline flex items-center gap-1"
                            >
                              #{property.listing_id}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                            <div className="text-xs text-slate-500">{property.status}</div>
                          </div>
                          <div className="col-span-2 text-right">
                            <span className="font-bold text-slate-900">${property.price?.toLocaleString()}</span>
                          </div>
                          <div className="col-span-1 text-center text-sm text-slate-700">
                            {property.sqft?.toLocaleString() || "—"}
                          </div>
                          <div className="col-span-2 text-right">
                            <span className="font-semibold text-blue-700">${property.rent?.toLocaleString()}/mo</span>
                            <div className="text-xs text-slate-500">NOI: ${property.noi?.toLocaleString()}/yr</div>
                          </div>
                          <div className="col-span-2 text-right">
                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${capRateColor(property.capRate)}`}>
                              {property.capRate?.toFixed(2)}%
                            </span>
                          </div>
                          <div className="col-span-2 text-right">
                            <Button
                              size="sm"
                              onClick={() => handleImportNyc(property)}
                              disabled={importingIds.has(property.property_id)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              {importingIds.has(property.property_id) ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-1" />
                                  Import
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <p className="mt-3 text-xs text-amber-600 bg-amber-50 p-2 rounded">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    Note: Financial estimates (Rent, HOA, Taxes, Cap Rate) are based on NYC market averages. View listing on StreetEasy for actual details.
                  </p>
                </div>
              )}
              
              <p className="mt-4 text-sm text-blue-700 bg-blue-50 border border-blue-200 p-3 rounded-lg">
                <CheckCircle className="w-4 h-4 inline mr-2" />
                NYC Real Estate API provides access to StreetEasy listings. Past sales data is excellent for comp analysis.
              </p>
            </TabsContent>
            <TabsContent value="streeteasy">
              {/* Mode Toggle */}
              <div className="flex gap-2 mb-6 p-1 bg-slate-100 rounded-lg w-fit">
                <Button
                  variant={streetEasyMode === "single" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setStreetEasyMode("single")}
                  className={streetEasyMode === "single" ? "bg-orange-600 hover:bg-orange-700 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Single Listing
                </Button>
                <Button
                  variant={streetEasyMode === "building" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setStreetEasyMode("building")}
                  className={streetEasyMode === "building" ? "bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}
                >
                  <Layers className="w-4 h-4 mr-2" />
                  New Development Building
                </Button>
              </div>

              {streetEasyMode === "single" ? (
                /* Single Listing Import */
                <div className="border border-slate-200 rounded-lg p-5 bg-slate-50">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center shadow-sm">
                      <Building2 className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">Import Single Listing</h3>
                      <p className="text-sm text-slate-500">Paste a StreetEasy listing URL to import</p>
                    </div>
                  </div>
                  
                  <form onSubmit={handleStreetEasyImport} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">StreetEasy Listing URL *</label>
                      <Input
                        value={streetEasyUrl}
                        onChange={(e) => setStreetEasyUrl(e.target.value)}
                        placeholder="https://streeteasy.com/building/one-wall-street/1807"
                        disabled={importingStreetEasy}
                        className="bg-white border-slate-300 focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                    
                    <Button type="submit" disabled={importingStreetEasy} className="bg-orange-600 hover:bg-orange-700 text-white font-semibold shadow-sm">
                      {importingStreetEasy ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Import Listing
                        </>
                      )}
                    </Button>
                  </form>
                  
                  <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-sm text-emerald-800 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span><strong>Auto-estimates:</strong> Rent, HOA, Taxes, NOI & Cap Rate calculated automatically</span>
                    </p>
                  </div>
                </div>
              ) : (
                /* Building Import Mode */
                <div className="border border-slate-200 rounded-lg p-5 bg-slate-50">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center shadow-sm">
                      <Layers className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">New Development Building</h3>
                      <p className="text-sm text-slate-500">Import multiple units from the same building</p>
                    </div>
                  </div>

                  {/* Create New Building or Select Existing */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left: Building Selection / Creation */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium">Select or Create Building</label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setBuildingModal(true)}
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          New Building
                        </Button>
                      </div>
                      
                      {buildings.length > 0 ? (
                        <Select value={selectedBuilding || ""} onValueChange={setSelectedBuilding}>
                          <SelectTrigger className={selectedBuilding ? "border-purple-300" : ""}>
                            <SelectValue placeholder="Select a building..." />
                          </SelectTrigger>
                          <SelectContent>
                            {buildings.map((b) => (
                              <SelectItem key={b.building_id} value={b.building_id}>
                                {b.name} ({b.unit_count || 0} units)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="p-4 bg-gray-50 rounded-lg text-center">
                          <p className="text-sm text-gray-500">No buildings yet. Create one to get started.</p>
                        </div>
                      )}

                      {/* Add Unit to Building */}
                      {selectedBuilding && (
                        <div className="p-4 bg-purple-50 rounded-lg space-y-3">
                          <label className="block text-sm font-medium text-purple-800">Add Unit to Building</label>
                          <div className="flex gap-2">
                            <Input
                              value={buildingUnitUrl}
                              onChange={(e) => setBuildingUnitUrl(e.target.value)}
                              placeholder="https://streeteasy.com/building/..."
                              disabled={addingUnit}
                              className="flex-1"
                            />
                            <Button
                              onClick={handleAddUnitToBuilding}
                              disabled={addingUnit || !buildingUnitUrl.trim()}
                              className="bg-purple-600 hover:bg-purple-700"
                            >
                              {addingUnit ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Plus className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-purple-600">
                            Paste StreetEasy URLs for each unit in the building. Financial estimates auto-calculated.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Right: Building List with Units */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium">Your Buildings</label>
                      <div className="max-h-[400px] overflow-y-auto space-y-2">
                        {buildings.length === 0 ? (
                          <div className="p-6 bg-gray-50 rounded-lg text-center">
                            <Layers className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">Create a building to start adding units</p>
                          </div>
                        ) : (
                          buildings.map((building) => (
                            <div 
                              key={building.building_id}
                              className={`border rounded-lg transition-all ${
                                selectedBuilding === building.building_id 
                                  ? "border-purple-400 bg-purple-50" 
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              <div 
                                className="p-3 flex items-center justify-between cursor-pointer"
                                onClick={() => {
                                  setSelectedBuilding(building.building_id);
                                  toggleBuildingExpand(building.building_id);
                                }}
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center">
                                    <Building2 className="w-4 h-4 text-purple-600" />
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-sm">{building.name}</h4>
                                    <p className="text-xs text-gray-500">{building.address}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                                    {building.unit_count || 0} units
                                  </span>
                                  {expandedBuilding === building.building_id ? (
                                    <ChevronUp className="w-4 h-4 text-gray-400" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                  )}
                                </div>
                              </div>
                              
                              {/* Expanded Building Details */}
                              <AnimatePresence>
                                {expandedBuilding === building.building_id && building.units && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="border-t border-gray-200 overflow-hidden"
                                  >
                                    {/* Building Stats */}
                                    {building.stats && building.stats.total_units > 0 && (
                                      <div className="p-3 bg-gradient-to-r from-purple-50 to-blue-50 grid grid-cols-3 gap-2 text-center">
                                        <div>
                                          <div className="text-xs text-gray-500">Price Range</div>
                                          <div className="text-sm font-semibold text-purple-700">
                                            ${(building.stats.price_range?.min / 1000000)?.toFixed(1)}M - ${(building.stats.price_range?.max / 1000000)?.toFixed(1)}M
                                          </div>
                                        </div>
                                        <div>
                                          <div className="text-xs text-gray-500">Sqft Range</div>
                                          <div className="text-sm font-semibold">
                                            {building.stats.sqft_range?.min?.toLocaleString()} - {building.stats.sqft_range?.max?.toLocaleString()}
                                          </div>
                                        </div>
                                        <div>
                                          <div className="text-xs text-gray-500">Cap Rate</div>
                                          <div className={`text-sm font-bold ${
                                            building.stats.cap_rate_range?.avg >= 4 ? 'text-green-600' : 
                                            building.stats.cap_rate_range?.avg >= 3 ? 'text-amber-600' : 'text-red-500'
                                          }`}>
                                            {building.stats.cap_rate_range?.min?.toFixed(1)}% - {building.stats.cap_rate_range?.max?.toFixed(1)}%
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Unit List */}
                                    <div className="p-2 space-y-1 max-h-[200px] overflow-y-auto">
                                      {building.units?.length > 0 ? building.units.map((unit) => (
                                        <div 
                                          key={unit.scraped_id}
                                          className="p-2 bg-white rounded border border-gray-100 flex items-center justify-between text-xs"
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className="font-medium">Unit {unit.unit_number || "?"}</span>
                                            <span className="text-gray-500">{unit.bedrooms}BR</span>
                                            <span className="text-gray-500">{unit.sqft?.toLocaleString()} SF</span>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <span className="font-semibold text-blue-600">
                                              ${(unit.price / 1000000)?.toFixed(2)}M
                                            </span>
                                            <span className={`px-1.5 py-0.5 rounded font-bold ${
                                              unit.calculated_cap_rate >= 4 ? 'bg-green-100 text-green-700' :
                                              unit.calculated_cap_rate >= 3 ? 'bg-amber-100 text-amber-700' :
                                              'bg-red-100 text-red-600'
                                            }`}>
                                              {unit.calculated_cap_rate?.toFixed(1)}%
                                            </span>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveUnitFromBuilding(unit.scraped_id, unit.unit_number, building.building_id);
                                              }}
                                              className="p-0.5 text-gray-400 hover:text-red-500 transition-colors"
                                              title="Remove from building"
                                            >
                                              <X className="w-3 h-3" />
                                            </button>
                                          </div>
                                        </div>
                                      )) : (
                                        <p className="text-center text-gray-400 py-2 text-xs">No units added yet</p>
                                      )}
                                    </div>
                                    
                                    {/* Building Actions */}
                                    <div className="p-2 border-t border-gray-100 flex justify-between">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openEditBuildingModal(building);
                                        }}
                                        className="text-purple-600 hover:text-purple-800 hover:bg-purple-50 text-xs"
                                      >
                                        <Edit className="w-3 h-3 mr-1" />
                                        Edit Details
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteBuilding(building.building_id, building.name);
                                        }}
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs"
                                      >
                                        <Trash2 className="w-3 h-3 mr-1" />
                                        Delete
                                      </Button>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <p className="text-sm text-indigo-800">
                      <strong>New Development Import:</strong> Create a building, then add multiple unit URLs. Each unit gets auto-estimates. Perfect for 1031 investors comparing units.
                    </p>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Realtor.com Search Tab */}
            <TabsContent value="realtor">
              <div className="border border-slate-200 rounded-lg p-5 bg-slate-50">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center shadow-sm">
                    <Search className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Search Realtor.com</h3>
                    <p className="text-sm text-slate-500">Search condos directly from Realtor.com's database</p>
                  </div>
                </div>
              
              <form onSubmit={handleRealtorSearch} className="space-y-5">
                {/* Primary Search Fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Location *</label>
                    <Input
                      value={realtorSearch.location}
                      onChange={(e) => setRealtorSearch({...realtorSearch, location: e.target.value})}
                      placeholder="e.g., Manhattan, NY or Miami, FL or 10001"
                      disabled={searchingRealtor}
                      className="bg-white border-slate-300 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Property Type</label>
                    <Select 
                      value={realtorSearch.property_type} 
                      onValueChange={(v) => setRealtorSearch({...realtorSearch, property_type: v})}
                    >
                      <SelectTrigger className="bg-white border-slate-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="condo">Condo</SelectItem>
                        <SelectItem value="apartment">Apartment</SelectItem>
                        <SelectItem value="single_family">Single Family</SelectItem>
                        <SelectItem value="multi_family">Multi Family</SelectItem>
                        <SelectItem value="any">Any</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Price & Size Filters */}
                <div className="p-4 bg-white border border-slate-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-slate-500" />
                    Price & Size Filters
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Min Price</label>
                      <Input
                        type="number"
                        value={realtorSearch.min_price}
                        onChange={(e) => setRealtorSearch({...realtorSearch, min_price: e.target.value})}
                        placeholder="500000"
                        disabled={searchingRealtor}
                        className="bg-slate-50 border-slate-200"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Max Price</label>
                      <Input
                        type="number"
                        value={realtorSearch.max_price}
                        onChange={(e) => setRealtorSearch({...realtorSearch, max_price: e.target.value})}
                        placeholder="5000000"
                        disabled={searchingRealtor}
                        className="bg-slate-50 border-slate-200"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Min Beds</label>
                      <Input
                        type="number"
                        value={realtorSearch.min_beds}
                        onChange={(e) => setRealtorSearch({...realtorSearch, min_beds: e.target.value})}
                        placeholder="1"
                        disabled={searchingRealtor}
                        className="bg-slate-50 border-slate-200"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Results Limit</label>
                      <Select 
                        value={String(realtorSearch.limit)} 
                        onValueChange={(v) => setRealtorSearch({...realtorSearch, limit: parseInt(v)})}
                      >
                        <SelectTrigger className="bg-slate-50 border-slate-200">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="30">30</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                
                {/* Year Built Filter */}
                <div className="p-4 bg-white border border-slate-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    Year Built & Cap Rate Filters
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Min Year Built</label>
                      <Input
                        type="number"
                        value={realtorSearch.min_year_built}
                        onChange={(e) => setRealtorSearch({...realtorSearch, min_year_built: e.target.value})}
                        placeholder="2010"
                        disabled={searchingRealtor}
                        className="bg-slate-50 border-slate-200"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Max Year Built</label>
                      <Input
                        type="number"
                        value={realtorSearch.max_year_built}
                        onChange={(e) => setRealtorSearch({...realtorSearch, max_year_built: e.target.value})}
                        placeholder="2025"
                        disabled={searchingRealtor}
                        className="bg-slate-50 border-slate-200"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-emerald-700 mb-1">Min Cap Rate (%)</label>
                      <Input
                        type="number"
                        step="0.1"
                        value={realtorSearch.min_cap_rate}
                        onChange={(e) => setRealtorSearch({...realtorSearch, min_cap_rate: e.target.value})}
                        placeholder="4.0"
                        disabled={searchingRealtor}
                        className="border-emerald-300 focus:border-emerald-500 bg-emerald-50"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Search Button */}
                <div className="flex gap-3">
                  <Button type="submit" disabled={searchingRealtor} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm flex-1">
                    {searchingRealtor ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Search Properties
                      </>
                    )}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setSaveSearchModal(true)}
                    disabled={!realtorSearch.location.trim()}
                    className="border-amber-400 text-amber-700 hover:bg-amber-50 font-medium"
                  >
                    <BookmarkPlus className="w-4 h-4 mr-1" />
                    Save
                  </Button>
                </div>
              </form>

              {/* Saved Searches */}
              {savedSearches.length > 0 && (
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Bookmark className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">Saved Searches</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {savedSearches.map((search) => (
                      <div 
                        key={search.search_id} 
                        className="group flex items-center gap-1 px-2 py-1 bg-white rounded border border-amber-200 hover:border-amber-400 transition-colors"
                      >
                        <button
                          onClick={() => handleLoadSearch(search)}
                          className="text-xs font-medium text-gray-700 hover:text-amber-700 flex items-center gap-1"
                        >
                          {search.last_used && (
                            <Clock className="w-3 h-3 text-gray-400" />
                          )}
                          {search.name}
                        </button>
                        <button
                          onClick={() => handleDeleteSearch(search.search_id, search.name)}
                          className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-red-500 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Realtor.com Search Results */}
              {realtorResults.length > 0 && (
                <div className="mt-6 border-t border-slate-200 pt-6">
                  {/* Results Header with Sort Controls */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-900">
                      Search Results ({realtorResults.length} properties)
                    </h3>
                    
                    {/* Sort Controls */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">Sort by:</span>
                      <div className="flex bg-slate-100 rounded-lg p-1 gap-1">
                        {[
                          { key: "capRate", label: "Cap Rate" },
                          { key: "price", label: "Price" },
                          { key: "sqft", label: "Sqft" },
                          { key: "pricePerSqft", label: "$/SF" },
                          { key: "rent", label: "Rent" }
                        ].map((sort) => (
                          <button
                            key={sort.key}
                            onClick={() => handleSort(sort.key)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1 transition-all ${
                              resultsSortBy === sort.key 
                                ? 'bg-white text-slate-900 shadow-sm' 
                                : 'text-slate-600 hover:text-slate-900'
                            }`}
                          >
                            {sort.label}
                            {resultsSortBy === sort.key && (
                              resultsSortOrder === "desc" 
                                ? <ArrowDown className="w-3 h-3" />
                                : <ArrowUp className="w-3 h-3" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Results Table Header */}
                  <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-2 bg-slate-100 rounded-t-lg text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    <div className="col-span-4">Property</div>
                    <div className="col-span-2 text-right cursor-pointer hover:text-slate-900" onClick={() => handleSort("price")}>
                      Price {resultsSortBy === "price" && (resultsSortOrder === "desc" ? "↓" : "↑")}
                    </div>
                    <div className="col-span-1 text-center cursor-pointer hover:text-slate-900" onClick={() => handleSort("sqft")}>
                      Sqft {resultsSortBy === "sqft" && (resultsSortOrder === "desc" ? "↓" : "↑")}
                    </div>
                    <div className="col-span-2 text-right cursor-pointer hover:text-slate-900" onClick={() => handleSort("rent")}>
                      Est. Rent {resultsSortBy === "rent" && (resultsSortOrder === "desc" ? "↓" : "↑")}
                    </div>
                    <div className="col-span-2 text-right cursor-pointer hover:text-slate-900" onClick={() => handleSort("capRate")}>
                      Cap Rate {resultsSortBy === "capRate" && (resultsSortOrder === "desc" ? "↓" : "↑")}
                    </div>
                    <div className="col-span-1"></div>
                  </div>
                  
                  {/* Results List */}
                  <div className="border border-slate-200 rounded-b-lg divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
                    {getSortedResults(realtorResults.map(p => ({
                      ...p,
                      ...estimatePropertyFinancials(p)
                    }))).map((property, idx) => (
                      <div key={property.property_id || idx} className="p-4 hover:bg-slate-50 transition-colors">
                        {/* Mobile Layout */}
                        <div className="md:hidden">
                          <div className="flex items-start gap-3">
                            <div className="w-20 h-16 rounded overflow-hidden bg-slate-200 flex-shrink-0">
                              {property.images?.[0] ? (
                                <img src={property.images[0]} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Image className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm text-slate-900 truncate">
                                {property.title || property.address || "Property"}
                              </h4>
                              <div className="flex flex-wrap gap-2 text-xs text-slate-500 mt-1">
                                <span className="font-bold text-slate-900">{formatPrice(property.price)}</span>
                                <span>{property.bedrooms}BR</span>
                                <span>{property.sqft?.toLocaleString()} SF</span>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-xs text-slate-500">Est. ${property.rent?.toLocaleString()}/mo</span>
                                <span className={`px-2 py-0.5 rounded text-xs font-bold ${capRateColor(property.capRate)}`}>
                                  {property.capRate?.toFixed(1)}%
                                </span>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleImportRealtor(property)}
                              disabled={importingIds.has(property.property_id)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              {importingIds.has(property.property_id) ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Desktop Layout - Table Row */}
                        <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                          <div className="col-span-4 flex items-center gap-3">
                            <div className="w-16 h-12 rounded overflow-hidden bg-slate-200 flex-shrink-0">
                              {property.images?.[0] ? (
                                <img src={property.images[0]} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Image className="w-4 h-4 text-slate-400" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <h4 className="font-medium text-sm text-slate-900 truncate">
                                {property.title || property.address || "Property"}
                              </h4>
                              <div className="flex gap-2 text-xs text-slate-500">
                                <span>{property.bedrooms}BR / {property.bathrooms}BA</span>
                                {property.year_built && <span>• Built {property.year_built}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="col-span-2 text-right">
                            <span className="font-bold text-slate-900">{formatPrice(property.price)}</span>
                            {property.sqft > 0 && (
                              <div className="text-xs text-slate-500">${Math.round(property.price / property.sqft)}/SF</div>
                            )}
                          </div>
                          <div className="col-span-1 text-center text-sm text-slate-700">
                            {property.sqft?.toLocaleString()}
                          </div>
                          <div className="col-span-2 text-right">
                            <span className="font-semibold text-blue-700">${property.rent?.toLocaleString()}/mo</span>
                            <div className="text-xs text-slate-500">NOI: ${property.noi?.toLocaleString()}/yr</div>
                          </div>
                          <div className="col-span-2 text-right">
                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${capRateColor(property.capRate)}`}>
                              {property.capRate?.toFixed(2)}%
                            </span>
                          </div>
                          <div className="col-span-1 text-right">
                            <Button
                              size="sm"
                              onClick={() => handleImportRealtor(property)}
                              disabled={importingIds.has(property.property_id)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              {importingIds.has(property.property_id) ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Download className="w-4 h-4 mr-1" />
                                  Import
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <p className="mt-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 p-3 rounded-lg">
                <CheckCircle className="w-4 h-4 inline mr-2" />
                Realtor.com API provides reliable access to property data. Imported listings will appear in "Pending" for review.
              </p>
              </div>
            </TabsContent>

            {/* URL Import Tab */}
            <TabsContent value="url">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#0066FF]/10 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-[#0066FF]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#1A1A2E]">Import Listing from URL</h2>
                  <p className="text-sm text-gray-500">Paste a listing URL from StreetEasy, Zillow, Compass, or Redfin</p>
                </div>
              </div>
              
              <form onSubmit={handleScrape} className="flex gap-3" data-testid="scrape-form">
                <div className="flex-1">
                  <Input
                    value={scrapeUrl}
                    onChange={(e) => setScrapeUrl(e.target.value)}
                    placeholder="https://streeteasy.com/building/123-main-st/1a"
                    className="w-full"
                    disabled={scraping}
                    data-testid="scrape-url-input"
                  />
                </div>
                <Button type="submit" disabled={scraping} className="btn-primary" data-testid="scrape-submit-btn">
                  {scraping ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Import Listing
                    </>
                  )}
                </Button>
              </form>
              
              <div className="mt-4 flex flex-wrap gap-2 text-xs text-gray-500">
                <span className="font-medium">Supported sites:</span>
                <span className="px-2 py-1 bg-gray-100 rounded">StreetEasy</span>
                <span className="px-2 py-1 bg-gray-100 rounded">Zillow</span>
                <span className="px-2 py-1 bg-gray-100 rounded">Compass</span>
                <span className="px-2 py-1 bg-gray-100 rounded">Redfin</span>
              </div>
              
              <p className="mt-3 text-xs text-amber-600 bg-amber-50 p-2 rounded">
                <AlertCircle className="w-3 h-3 inline mr-1" />
                Note: Some sites may block automated imports. Use "Realtor.com Search" or "Manual Entry" if this fails.
              </p>
            </TabsContent>

            {/* Manual Entry Tab */}
            <TabsContent value="manual">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Edit className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#1A1A2E]">Manual Entry</h2>
                  <p className="text-sm text-slate-500">Add a listing manually by filling in the details</p>
                </div>
              </div>
              
              <Button 
                onClick={openManualEntry}
                className="bg-violet-600 hover:bg-violet-700 text-white font-semibold shadow-sm"
                data-testid="manual-entry-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Listing
              </Button>
              
              <p className="mt-4 text-sm text-violet-700 bg-violet-50 border border-violet-200 p-3 rounded-lg">
                <Edit className="w-4 h-4 inline mr-2" />
                Use this option to add listings from sources that aren't supported by automated import.
              </p>
            </TabsContent>
          </Tabs>
        </motion.div>

        {/* Imported Listings Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-testid="scraped-listings-container">
          <div className="flex items-center justify-between p-5 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-bold text-slate-900">Imported Listings</h3>
              <div className="flex gap-2" data-testid="status-filter-buttons">
                {["pending", "approved", "rejected", ""].map((status) => (
                  <button
                    key={status || "all"}
                    onClick={() => setStatusFilter(status)}
                    data-testid={`status-filter-${status || "all"}`}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                      statusFilter === status
                        ? 'bg-slate-900 text-white shadow-sm'
                        : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                    }`}
                  >
                    {status === "" ? "All" : status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={fetchListings} data-testid="refresh-listings-btn" className="border-slate-300">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {listings.length === 0 ? (
            <div className="p-16 text-center" data-testid="empty-listings-message">
              <Globe className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">No imported listings found</p>
              <p className="text-sm text-slate-400 mt-2">Import a listing using one of the methods above</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100" data-testid="listings-list">
              {listings.map((listing) => (
                <motion.div
                  key={listing.scraped_id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-5 hover:bg-slate-50 transition-colors"
                  data-testid={`listing-item-${listing.scraped_id}`}
                >
                  <div className="flex gap-5">
                    {/* Image */}
                    <div className="w-36 h-28 rounded-lg overflow-hidden bg-slate-200 flex-shrink-0 shadow-sm">
                      {listing.images?.[0] ? (
                        <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-10 h-10 text-slate-300" />
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h4 className="font-semibold text-[#1A1A2E] truncate">
                            {listing.title || listing.address || "Untitled Listing"}
                          </h4>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                            <MapPin className="w-3 h-3" />
                            <span>{listing.address || "Address not found"}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {listing.status === "pending" && (
                            <span className="px-2.5 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                              Pending Review
                            </span>
                          )}
                          {listing.status === "approved" && (
                            <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 text-xs font-semibold rounded-full">
                              Approved
                            </span>
                          )}
                          {listing.status === "rejected" && (
                            <span className="px-2.5 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                              Rejected
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4 text-slate-400" />
                          <span className="font-bold text-slate-900">{formatPrice(listing.price)}</span>
                        </div>
                        {listing.bedrooms && (
                          <div className="flex items-center gap-1 text-slate-600">
                            <Bed className="w-4 h-4 text-slate-400" />
                            <span>{listing.bedrooms} bed</span>
                          </div>
                        )}
                        {listing.bathrooms && (
                          <div className="flex items-center gap-1 text-slate-600">
                            <Bath className="w-4 h-4 text-slate-400" />
                            <span>{listing.bathrooms} bath</span>
                          </div>
                        )}
                        {listing.sqft && (
                          <div className="flex items-center gap-1 text-slate-600">
                            <Square className="w-4 h-4 text-slate-400" />
                            <span>{listing.sqft.toLocaleString()} SF</span>
                          </div>
                        )}
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-medium rounded">
                          {listing.market?.toUpperCase() || "NYC"}
                        </span>
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                          {listing.source_site}
                        </span>
                      </div>

                      {/* Financial Estimates */}
                      {(listing.projected_monthly_rent || listing.monthly_common_charges || listing.monthly_taxes) && (
                        <div className="mt-3 p-3 bg-slate-100 border border-slate-200 rounded-lg">
                          <div className="flex flex-wrap gap-5 text-sm">
                            {listing.projected_monthly_rent > 0 && (
                              <div>
                                <span className="text-slate-500 text-xs">Est. Rent:</span>
                                <span className="ml-1 font-semibold text-slate-900">
                                  ${listing.projected_monthly_rent.toLocaleString()}/mo
                                </span>
                              </div>
                            )}
                            {listing.monthly_common_charges > 0 && (
                              <div>
                                <span className="text-gray-500">HOA:</span>
                                <span className="ml-1 font-medium text-gray-700">
                                  ${listing.monthly_common_charges.toLocaleString()}/mo
                                </span>
                              </div>
                            )}
                            {listing.monthly_taxes > 0 && (
                              <div>
                                <span className="text-gray-500">Taxes:</span>
                                <span className="ml-1 font-medium text-gray-700">
                                  ${listing.monthly_taxes.toLocaleString()}/mo
                                </span>
                              </div>
                            )}
                            {listing.price > 0 && listing.projected_monthly_rent > 0 && (() => {
                              const annualRent = listing.projected_monthly_rent * 12;
                              const annualExpenses = ((listing.monthly_common_charges || 0) + (listing.monthly_taxes || 0)) * 12;
                              const noi = annualRent - annualExpenses;
                              const capRate = (noi / listing.price) * 100;
                              return (
                                <>
                                  <div className="border-l border-blue-200 pl-3">
                                    <span className="text-gray-500">NOI:</span>
                                    <span className="ml-1 font-semibold text-green-600">
                                      ${noi.toLocaleString()}/yr
                                    </span>
                                  </div>
                                  <div className="border-l border-blue-200 pl-3">
                                    <span className="text-gray-500">Cap Rate:</span>
                                    <span className={`ml-1 font-bold ${capRate >= 4 ? 'text-green-600' : capRate >= 3 ? 'text-amber-600' : 'text-red-500'}`}>
                                      {capRate.toFixed(2)}%
                                    </span>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}

                      {/* Missing fields warning */}
                      {listing.status === "pending" && (
                        <div className="mt-2">
                          {(!listing.price || !listing.bedrooms || !listing.sqft) && (
                            <div className="flex items-center gap-2 text-xs text-amber-600">
                              <AlertCircle className="w-3 h-3" />
                              <span>
                                Missing: 
                                {!listing.price && " Price"}
                                {!listing.bedrooms && " Beds"}
                                {!listing.sqft && " Sqft"}
                                {!listing.monthly_common_charges && " Common Charges"}
                                {!listing.projected_monthly_rent && " Projected Rent"}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-3">
                        {listing.status === "pending" && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleApprove(listing.scraped_id)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`approve-btn-${listing.scraped_id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditModal(listing)}
                              data-testid={`edit-btn-${listing.scraped_id}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit & Complete
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleReject(listing.scraped_id)}
                              data-testid={`reject-btn-${listing.scraped_id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        <a
                          href={listing.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-400 hover:text-[#0066FF] hover:bg-blue-50 rounded transition-colors"
                          data-testid={`source-link-${listing.scraped_id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => handleDelete(listing.scraped_id)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          data-testid={`delete-btn-${listing.scraped_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      <EditListingModal
        editModal={editModal} setEditModal={setEditModal}
        isManualEntry={isManualEntry} setIsManualEntry={setIsManualEntry}
        editingListing={editingListing} editForm={editForm} setEditForm={setEditForm}
        calculateCapRate={calculateCapRate} handleAutoEstimate={handleAutoEstimate}
        estimating={estimating} handleSaveEdit={handleSaveEdit}
        handleSaveManualEntry={handleSaveManualEntry} savingManual={savingManual}
        handleApprove={handleApprove}
      />

      <SaveSearchModal
        saveSearchModal={saveSearchModal} setSaveSearchModal={setSaveSearchModal}
        saveSearchName={saveSearchName} setSaveSearchName={setSaveSearchName}
        handleSaveSearch={handleSaveSearch} savingSearch={savingSearch}
      />

      <BuildingModal
        buildingModal={buildingModal} closeBuildingModal={closeBuildingModal}
        editingBuildingId={editingBuildingId} buildingForm={buildingForm}
        setBuildingForm={setBuildingForm} creatingBuilding={creatingBuilding}
        handleUpdateBuilding={handleUpdateBuilding} handleCreateBuilding={handleCreateBuilding}
      />
    </div>
  );
}
